import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import DatasetSelectModal from '../components/DatasetSelectModal';
import DatasetCropView from '../components/DatasetCropView';
import WaitBehaviorModal from '../components/WaitBehaviorModal';
import { fetchScenario, updateScenario } from '../services/lvcService';
import { fetchUserProfiles } from '../services/usersService';
import { useError } from '../contexts/ErrorContext';
import './LVCAgentPage.css';

const LVCAgentPage = () => {
  const navigate = useNavigate();
  const { scenarioId, agentIndex } = useParams();
  const { addError } = useError();
  const [agent, setAgent] = useState(null);
  const [behaviors, setBehaviors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAddBehavior, setShowAddBehavior] = useState(false);
  const [newBehaviorType, setNewBehaviorType] = useState('wait');
  const [showDatasetSelect, setShowDatasetSelect] = useState(false);
  const [showDatasetCrop, setShowDatasetCrop] = useState(false);
  const [showWaitModal, setShowWaitModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);

  useEffect(() => {
    const loadAgent = async () => {
      if (!scenarioId || agentIndex === void 0) {
        navigate('/lvc');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const scenario = await fetchScenario(scenarioId);
        const virtualAgents = scenario.virtual_agents || [];
        const agentIdx = parseInt(agentIndex, 10);
        
        if (agentIdx < 0 || agentIdx >= virtualAgents.length) {
          setError('Agent not found');
          return;
        }

        const agentData = virtualAgents[agentIdx];
        setAgent(agentData);
        setBehaviors(agentData.behaviors || []);
        setUsers(await fetchUserProfiles([agentData.uid], addError));
      } catch (err) {
        console.error('Failed to load agent:', err);
        setError(err.message || 'Failed to load agent');
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [scenarioId, agentIndex, navigate, addError]);

  const handleSave = async () => {
    if (!scenarioId || agentIndex === void 0) return;

    try {
      setSaving(true);
      setSaveSuccess(false);
      
      const scenario = await fetchScenario(scenarioId);
      const virtualAgents = [...(scenario.virtual_agents || [])];
      const agentIdx = parseInt(agentIndex, 10);
      
      virtualAgents[agentIdx] = {
        ...virtualAgents[agentIdx],
        behaviors: behaviors
      };

      await updateScenario(scenarioId, {
        virtual_agents: virtualAgents,
        live_agents: scenario.live_agents || []
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save agent:', err);
      alert(`Failed to save agent: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBehavior = () => {
    if (newBehaviorType === 'wait') {
      // Wait behavior - show wait modal
      setShowAddBehavior(false);
      setShowWaitModal(true);
    } else if (newBehaviorType === 'datasetview') {
      // Dataset view - show dataset selection modal
      setShowAddBehavior(false);
      setShowDatasetSelect(true);
    }
  };

  const handleWaitBehaviorSave = (waitBehavior) => {
    setBehaviors([...behaviors, waitBehavior]);
    setShowWaitModal(false);
    setNewBehaviorType('wait');
  };

  const handleDatasetSelect = (dataset) => {
    setSelectedDataset(dataset);
    setShowDatasetSelect(false);
    setShowDatasetCrop(true);
  };

  const handleDatasetCropSave = (cropData) => {
    const newBehavior = {
      type: 'datasetview',
      dataset_id: cropData.dataset_id,
      start_time: cropData.start_time,
      end_time: cropData.end_time
    };
    setBehaviors([...behaviors, newBehavior]);
    setShowDatasetCrop(false);
    setSelectedDataset(null);
    setNewBehaviorType('wait');
  };

  const handleRemoveBehavior = (index) => {
    setBehaviors(behaviors.filter((_, i) => i !== index));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--:--';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="lvc-agent-page">
        <div className="agent-loading">Loading agent...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lvc-agent-page">
        <div className="agent-error">
          <p>Error: {error}</p>
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate(`/lvc/${scenarioId}`)}
          >
            Back to Scenario
          </Button>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="lvc-agent-page">
        <div className="agent-error">
          <p>Agent not found</p>
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate(`/lvc/${scenarioId}`)}
          >
            Back to Scenario
          </Button>
        </div>
      </div>
    );
  }

  const getUserByUid = (uid) => {
    return users.find(user => user.uid === uid);
  };

  const agentUser = getUserByUid(agent.uid);
  const agentDisplayName = agentUser ? agentUser.name : agent.uid;

  return (
    <div className="lvc-agent-page">
      <header className="lvc-agent-header">
        <div className="lvc-agent-header-content">
          <div className="lvc-agent-header-left">
            <Button
              variant="outline"
              size="medium"
              onClick={() => navigate(`/lvc/${scenarioId}`)}
              className="back-button"
            >
              ←
            </Button>
            <h1>{agentDisplayName}</h1>
          </div>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save'}
          </Button>
        </div>
      </header>
      <main className="lvc-agent-main">
        <div className="agent-details">
          <div className="behaviors-section">
            <div className="behaviors-header">
              <h2>Behaviors</h2>
              {!showAddBehavior ? (
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => setShowAddBehavior(true)}
                >
                  Add Behavior
                </Button>
              ) : (
                <div className="add-behavior-form">
                  <select
                    value={newBehaviorType}
                    onChange={(e) => setNewBehaviorType(e.target.value)}
                    className="behavior-type-select"
                  >
                    <option value="wait">Wait</option>
                    <option value="datasetview">Dataset View</option>
                  </select>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleAddBehavior}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => {
                      setShowAddBehavior(false);
                      setNewBehaviorType('wait');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            <div className="behaviors-list">
              {behaviors.length === 0 ? (
                <p className="empty-message">No behaviors added yet. Click "Add Behavior" to get started.</p>
              ) : (
                <ul className="behaviors-list-items">
                  {behaviors.map((behavior, index) => (
                    <li key={index} className="behavior-item">
                      <div className="behavior-info">
                        <span className="behavior-type">{behavior.type}</span>
                        {behavior.type === 'wait' && (
                          <div className="behavior-details">
                            {behavior.duration !== void 0 && (
                              <span className="behavior-detail">Duration: {behavior.duration}s</span>
                            )}
                            {behavior.latitude !== void 0 && behavior.longitude !== void 0 ? (
                              <span className="behavior-detail">Location: {behavior.latitude.toFixed(6)}, {behavior.longitude.toFixed(6)}</span>
                            ) : (
                              <span className="behavior-detail">No location</span>
                            )}
                          </div>
                        )}
                        {behavior.type === 'datasetview' && (
                          <div className="behavior-details">
                            {behavior.dataset_id && (
                              <span className="behavior-detail">Dataset: {behavior.dataset_id}</span>
                            )}
                            {behavior.start_time && behavior.end_time && (
                              <span className="behavior-detail">
                                Time: {formatTime(behavior.start_time)} - {formatTime(behavior.end_time)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => handleRemoveBehavior(index)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
      <DatasetSelectModal
        isOpen={showDatasetSelect}
        onClose={() => {
          setShowDatasetSelect(false);
          setShowAddBehavior(true);
        }}
        onSelect={handleDatasetSelect}
      />
      <DatasetCropView
        isOpen={showDatasetCrop}
        onClose={() => {
          setShowDatasetCrop(false);
          setSelectedDataset(null);
          setShowAddBehavior(true);
        }}
        dataset={selectedDataset}
        onSave={handleDatasetCropSave}
      />
      <WaitBehaviorModal
        isOpen={showWaitModal}
        onClose={() => {
          setShowWaitModal(false);
          setShowAddBehavior(true);
        }}
        onSave={handleWaitBehaviorSave}
      />
    </div>
  );
};

export default LVCAgentPage;
